import { useState, useEffect } from 'react';
import { VStack, Text, Heading, HStack, ScrollView } from 'native-base';

// Assets
import { theme } from '@breakroom-nx/assets';

// Redux
import { useAppDispatch, closeModal, openModal } from '@breakroom-nx/redux';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@breakroom-nx/apis';

// Components
import {
  ModalWrapper,
  PrimaryButton,
  TertiaryButton,
  CustomCheckbox,
  TextView,
  EntityBeingReported,
} from '../../';

import * as Yup from 'yup';
import { Formik, ErrorMessage } from 'formik';

// Types
import { InputType, ReportOptions, Status } from '@breakroom-nx/helpers';

const OtherScheme = Yup.object().shape({
  content: Yup.string()
    .required(`You selected "Other". Please explain your reason for reporting.`)
    .max(250, 'Sorry, there is a maximum limit of 250 characters'),
});

interface ReportFormValues {
  content: string;
}

export const Report = (props: any) => {
  const uid: string = props.uid;
  const name: string = props.name;
  const entity: EntityBeingReported = props.entity;

  const appDispatch = useAppDispatch();

  const initialValues: ReportFormValues = { content: '' };
  const [showOther, setShowOther] = useState(false);
  const sendFeedback = httpsCallable(functions, 'user-sendfeedback');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  // Update showOther if "Other" is selected
  useEffect(() => {
    setShowOther(selectedOptions.includes(ReportOptions.Other));
  }, [selectedOptions]);

  const handleCheckboxSelect = (option: string) => {
    setSelectedOptions((prev) => {
      if (prev.includes(option)) {
        return prev.filter((o) => o !== option);
      } else {
        return [...prev, option];
      }
    });
  };

  async function submitReport(upvote: boolean, report?: string) {
    setSubmitting(true);
    await sendFeedback({
      uid: uid,
      upvote: upvote,
      report: selectedOptions,
    })
      .then(() => {
        setSubmitting(false);
        appDispatch(
          openModal({
            modalType: 'Alert',
            modalProps: {
              status: Status.success,
              title: 'Your report has been submitted',
            },
          })
        );
      })
      .catch((err) => {
        setSubmitting(false);
        appDispatch(
          openModal({
            modalType: 'Alert',
            modalProps: {
              status: Status.error,
              title: 'Error submitting report, please try again.',
            },
          })
        );
      });
  }

  return (
    <ModalWrapper disabled>
      <VStack
        bg={'surface.100'}
        space={3}
        textAlign={'center'}
        style={{
          borderColor: theme.colors.surface[200],
          borderWidth: 1,
          shadowColor: theme.colors.surface[100],
          shadowOpacity: 0.25,
          shadowRadius: 20,
          shadowOffset: {
            width: 10,
            height: 20,
          },
        }}
      >
        <ScrollView
          flex={1}
          borderRadius={25}
          overflow={'hidden'}
          maxHeight={'60vh'}
          showsVerticalScrollIndicator={true}
        >
          <VStack space={3} padding={30}>
            <Formik
              initialValues={initialValues}
              validationScheme={OtherScheme}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                // console.log(`Values [Content: ${values.content}]`);
                // const newFeedback = new Feedback({
                //   creatorUID: currentUser?.uid,
                //   content: values.content,
                // });
                // if (!feedbackType) {
                // } else {
                //   sendFeedback({
                //     user: currentUser,
                //     feedback: newFeedback,
                //     type: feedbackType,
                //   })
                //     .unwrap()
                //     .then((data) => {
                //       resetForm();
                //       dispatch(toggleForm());
                //       dispatch(
                //         openModal({
                //           modalType: 'Alert',
                //           modalProps: {
                //             status: Status.success,
                //             title: 'Feedback Received!',
                //           },
                //         })
                //       );
                //     })
                //     .catch((error) => {
                //       dispatch(
                //         openModal({
                //           modalType: 'Alert',
                //           modalProps: {
                //             status: Status.error,
                //             title:
                //               'Error submitting feedback. Please try again.',
                //           },
                //         })
                //       );
                //     });
                // }
              }}
            >
              {({ values, handleChange, handleBlur, handleSubmit }) => (
                <VStack space={5}>
                  <VStack alignItems={'stretch'} space={5}>
                    <Heading fontSize={30} lineHeight={34}>
                      The Ministry of Reports
                    </Heading>
                    <Text
                      color={theme.colors.surface[500]}
                      fontSize={16}
                      fontWeight={600}
                    >
                      {`What has `}
                      <Text
                        color={theme.colors.surface[600]}
                        fontWeight={'bold'}
                      >
                        {name}
                      </Text>
                      {` done?`}
                    </Text>
                    <VStack space={2} ml={3}>
                      {(
                        Object.values(ReportOptions) as Array<
                          keyof typeof ReportOptions
                        >
                      ).map((option, index) => {
                        return (
                          <CustomCheckbox
                            title={option}
                            checked={selectedOptions.includes(option)}
                            onCheckboxSelect={() =>
                              handleCheckboxSelect(option)
                            }
                            key={index}
                            otherSelected={
                              selectedOptions.length > 0 &&
                              !selectedOptions.includes(option)
                            }
                          />
                        );
                      })}
                    </VStack>
                    {showOther && (
                      <VStack>
                        <TextView
                          disabled={isSubmitting}
                          type={InputType.Text}
                          value={values.content}
                          onChangeText={handleChange('content')}
                          onBlur={handleBlur('content')}
                          isRequired={true}
                          height={150}
                          placeholderTextColor={'surface.600'}
                          inactiveTextColor={'surface.600'}
                          placeholder={
                            'You selected "Other". Please explain your reason for reporting.'
                          }
                        />
                        <Text variant={'error'}>
                          <ErrorMessage name="content" />
                        </Text>
                      </VStack>
                    )}
                  </VStack>

                  <VStack space={3}>
                    <PrimaryButton
                      disabled={selectedOptions.length === 0 || isSubmitting}
                      isLoading={isSubmitting}
                      text={'Submit Report'}
                      onPress={() => {
                        submitReport(false);
                      }}
                    />

                    <TertiaryButton
                      disabled={isSubmitting}
                      color={theme.colors.red[200]}
                      text={'Cancel'}
                      onPress={() => {
                        appDispatch(closeModal());
                      }}
                    />
                  </VStack>
                </VStack>
              )}
            </Formik>
          </VStack>
        </ScrollView>
      </VStack>
    </ModalWrapper>
  );
};
